// The Materials page's stock actions: ONE copy, used by the desktop Materials page
// (pages/MaterialEntryPage.jsx) and the phone (mobile/screens/MaterialsScreen.jsx).
// docs/mobile-pwa-plan.md "One copy of every action". Moved from the page word for word;
// pages/MaterialEntryPage.actions.test.jsx pins them. Actions take `ui` (features/desktopUi.js |
// mobile/ui/phoneUi.js): alert, confirm, prompt, pin. A printed document (the defect's sheet label, the
// transfer receipt) goes to `printPdf(blob)` when the caller has one (the desktop's print dialog); a
// phone gets its URL back to offer from a tap.
import config from '../../config.js';
import { apiErrorMessage, detailToString, parseApiError } from '../../utils/apiError';
import { isTransferableMtn } from '../../utils/mtnLot';
import { parseLotNumber } from '../../utils/beacons';

const API_BASE = config.API_BASE_URL;

export const TRANSACTION_REASONS = Object.freeze(['Inventory Correction', 'Defective', 'Consumed with no job', 'Manual QoH Correction']);

/** Add a stock transaction (a DELTA, not a new total) after the PIN. Resolves {done, newQuantity?}. */
export async function addMaterialTransaction({ materialId, delta, reason, jobId = '', ui }) {
  if (!materialId) {
    await ui.alert('Select or create a material first.');
    return { done: false };
  }
  if (!delta || !reason) {
    await ui.alert('Enter a delta and reason.');
    return { done: false };
  }
  const pin = await ui.pin('Enter your 4-digit PIN:');
  if (!pin) {
    await ui.alert('Transaction cancelled. PIN is required.');
    return { done: false };
  }
  const payload = {
    delta: parseInt(delta, 10) || 0,
    reason,
    job_id: jobId ? parseInt(jobId, 10) : undefined,
    entered_pin: pin,
  };
  try {
    const res = await fetch(`${API_BASE}/materials/${materialId}/transactions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await apiErrorMessage(res, 'Failed to record the transaction'));
    const data = await res.json();
    await ui.alert(`Transaction created. Old qty=${data.old_quantity}, new qty=${data.new_quantity}.`);
    return { done: true, newQuantity: data.new_quantity };
  } catch (err) {
    await ui.alert('Failed to add transaction: ' + err.message);
    return { done: false };
  }
}

/** The defect's sheet label (a PDF). */
export const defectLabelUrl = (defectId) => `${API_BASE}/defects/${defectId}/sheet_label`;

/** jobq-api's "which tracking number?" for a material defect (POST /defects 400 detail
 *  {error: 'defect_needs_lot', message, lots}: every sheet of the material in stock is on a lot and more
 *  than one could be it), from the response text; null for any other answer. */
export function defectLotQuestion(text) {
  try {
    const d = JSON.parse(text).detail;
    if (d && typeof d === 'object' && d.error === 'defect_needs_lot') {
      return { message: d.message || 'Which tracking number are the defective sheets from?',
               lots: Array.isArray(d.lots) ? d.lots : [] };
    }
  } catch (_) { /* not JSON */ }
  return null;
}

const textMessage = (text, fallback) => {
  try { return detailToString(JSON.parse(text).detail) || fallback; } catch (_) { return text || fallback; }
};

/** Record defective sheets of a material (how many, why, the PIN; POST /defects, workstation Laser, type
 *  sheet), then its sheet label: printed through printPdf(blob) when given, else returned as `labelUrl`.
 *  When the API asks which lot (since 2026-10-20: every sheet in stock is on a lot), the operator types the
 *  TRK (nothing pre-filled) and it is sent again with `mtn_id`. Resolves {done, defectId?, labelUrl?}. */
export async function recordMaterialDefect({ materialId, ui, printPdf = null }) {
  if (!materialId) {
    await ui.alert('Select a material first.');
    return { done: false };
  }
  const qtyStr = await ui.prompt('How many units of this material are defective?', '1');
  if (!qtyStr) return { done: false };
  const qty = parseInt(qtyStr, 10) || 0;
  if (qty < 1) return { done: false };
  const reason = await ui.prompt('Reason for defect?', 'Damaged Material');
  if (!reason) return { done: false };
  const pin = await ui.pin('Enter your 4-digit PIN:');
  if (!pin) return { done: false };

  let defectId = null;
  try {
    let mtnId = null;
    let res;
    for (;;) {
      const payload = {
        workstation: 'Laser',
        defect_reason: reason,
        defective_quantity: qty,
        defect_type: 'sheet',
        pin,
        material_id: materialId,
        ask_for_lot: true,                  // this page answers "which tracking number?" (below)
        ...(mtnId ? { mtn_id: mtnId } : {}),
      };
      res = await fetch(`${API_BASE}/defects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok || res.status !== 400 || mtnId) break;
      const text = await res.text();
      const ask = defectLotQuestion(text);
      if (!ask) throw new Error(textMessage(text, 'Failed to record the defect'));
      // no pre-fill: pressing Enter must never pick a lot (independent review 2026-10-20)
      const answer = await ui.prompt(`${ask.message}\n\nTracking number (TRK):`, '');
      if (!answer) return { done: false };
      mtnId = parseLotNumber(answer);
      if (!mtnId) {
        await ui.alert(`"${answer}" is not a tracking number. Nothing was recorded.`);
        return { done: false };
      }
    }
    if (!res.ok) throw new Error(await apiErrorMessage(res, 'Failed to record the defect'));
    const data = await res.json();
    defectId = data.defect_id;
    await ui.alert('Defect recorded! defect_id=' + data.defect_id
                   + (data.mtn_id ? ` (TRK ${String(data.mtn_id).padStart(6, '0')})` : ''));
    if (!printPdf) return { done: true, defectId, labelUrl: defectLabelUrl(defectId) };
    const labelRes = await fetch(defectLabelUrl(defectId));
    if (!labelRes.ok) throw new Error(await apiErrorMessage(labelRes, 'Failed to fetch the label PDF'));
    const pdfBlob = await labelRes.blob();
    printPdf(pdfBlob);
    return { done: true, defectId };
  } catch (err) {
    await ui.alert('Error recording defect or printing label: ' + err.message);
    // A defect that was recorded stays recorded even when its label didn't print.
    return { done: false, defectId };
  }
}

/** A transfer's receipt (a PDF). */
export const transferReceiptUrl = (transferId) => `${API_BASE}/materials/transfer/${transferId}/receipt`;

/** Move stock to another material record (the same steel under another owner), from a chosen lot when the
 *  material has lots. The checks (in the desktop's words), the PIN, POST /materials/transfer (when other
 *  orders hold the sheets by earmark, the API's question through ui.confirm, then once more with
 *  allow_earmarked_lot; No = `cancelled` + `message` "Transfer cancelled: …", no alert), the success
 *  alert (+ the inter-company invoice email line), then the receipt: printed through printPdf(blob) when
 *  given, else returned as `receiptUrl`. Resolves {done, receiptUrl?, needsLot?, message?}: `needsLot` with
 *  the API's words when it asks for a source lot (re-read the lots; the desktop shows it in its banner),
 *  `message` = "Transfer failed: …" for any other refusal (already alerted). */
export async function transferStock({
  sourceMaterialId, destinationMaterialId, createDestinationForOwnerId = null, quantity, reason, sourceMtnId = '', onHand, lots = [], ui,
  printPdf = null, setBusy = () => {},
}) {
  // createDestinationForOwnerId (2026-10-29): no destination picked; the API uses that owner's material of the same
  // steel, or creates a copy of the source for it, in the same transaction as the move.
  if (!sourceMaterialId || (!destinationMaterialId && !createDestinationForOwnerId) || !quantity || !reason) {
    await ui.alert('Please select a destination material, quantity, and reason.');
    return { done: false };
  }
  const qty = parseInt(quantity, 10);
  if (isNaN(qty) || qty <= 0) {
    await ui.alert('Quantity must be a positive number.');
    return { done: false };
  }
  const onHandStock = parseInt(onHand, 10) || 0;
  if (qty > onHandStock) {
    await ui.alert(`Transfer quantity (${qty}) exceeds quantity on hand (${onHandStock}).`);
    return { done: false };
  }
  // Source lot gate: when the material has open (active / unused) lots, the stack drawn from must be named
  // so the new lot inherits its heat / PO; when every lot is closed out, say so instead of a doomed request.
  const transferable = (lots || []).filter(isTransferableMtn);
  if (!sourceMtnId) {
    if (transferable.length > 0) {
      await ui.alert("This material has tracked MTNs. Please select which MTN stack you're drawing from.");
      return { done: false };
    }
    if ((lots || []).length > 0) {
      await ui.alert(
        'Every MTN on this material is exhausted, transferred out, returned, or reconciled — '
        + 'there is no open stack to draw from. Receive or correct a lot before transferring.',
      );
      return { done: false };
    }
  }
  if (sourceMtnId) {
    const picked = transferable.find((m) => String(m.mtn_id) === String(sourceMtnId));
    if (picked && qty > (picked.remaining_qty || 0)) {
      await ui.alert(`Transfer quantity (${qty}) exceeds the selected MTN's remaining qty (${picked.remaining_qty}).`);
      return { done: false };
    }
  }
  const pin = await ui.pin('Enter your 4-digit PIN to confirm the transfer:');
  if (!pin) {
    await ui.alert('Transfer cancelled. PIN is required.');
    return { done: false };
  }

  setBusy(true);
  const payload = {
    source_material_id: sourceMaterialId,
    ...(createDestinationForOwnerId
      ? { create_destination_for_owner_id: parseInt(createDestinationForOwnerId, 10) }
      : { destination_material_id: parseInt(destinationMaterialId, 10) }),
    quantity: qty,
    reason,
    pin,
  };
  if (sourceMtnId) payload.source_mtn_id = parseInt(sourceMtnId, 10);
  const post = (body) => fetch(`${API_BASE}/materials/transfer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  try {
    let res = await post(payload);
    for (let asked = false; !res.ok;) {
      // Read as TEXT first: a gateway 502 is an HTML page, and res.json() would mask the real failure.
      const rawBody = await res.text().catch(() => '');
      let detail = null;
      try { detail = JSON.parse(rawBody).detail; } catch { detail = null; }
      if (detail && typeof detail === 'object' && detail.error === 'source_mtn_id_required') {
        return { done: false, needsLot: true, message: detailToString(detail) || 'Source MTN required.' };
      }
      // Other orders hold these sheets by earmark (2026-10-16). The API's sentence names them and
      // ends "... confirm to transfer it anyway; that releases the earmark ...". OK sends it again
      // with the rows it showed (allocation_ids): the API releases only those, and asks again
      // (a second 400, shown as a failure) if the earmarks changed in between. Asked once.
      if (!asked && detail && typeof detail === 'object' && detail.error === 'earmarked_lot') {
        asked = true;
        const ok = await ui.confirm(
          detailToString(detail) || 'These sheets are earmarked for another order. Transfer them and release the earmark?',
          { title: 'Release the earmark?', confirmLabel: 'Transfer and release', danger: true },
        );
        if (!ok) return { done: false, cancelled: true, message: 'Transfer cancelled: the sheets are earmarked.' };
        res = await post({
          ...payload, allow_earmarked_lot: true, allow_earmarked_allocation_ids: detail.allocation_ids || [],
        });
        continue;
      }
      throw new Error(parseApiError(rawBody, 'Transfer failed', res.status));
    }
    const data = await res.json();
    let successMsg = `Success! Transferred ${data.quantity_transferred} units.`;
    if (data.destination_created) {
      successMsg += `\n\nCreated ${data.destination_material_code} for the new owner.`;
    } else if (createDestinationForOwnerId && data.destination_material_code) {
      successMsg += `\n\nMoved to ${data.destination_material_code}, which the owner already had.`;
    }
    const released = (data.earmarks_released || []).length;
    if (released) {
      successMsg += `\n\nReleased ${released} earmark${released === 1 ? '' : 's'}`
        + `${data.earmarks_released_for ? `: ${data.earmarks_released_for}` : ''}.`;
    }
    if (data.invoice_email && data.invoice_email.sent) {
      successMsg += `\n\n📧 Inter-company invoice automatically emailed to ${data.invoice_email.to}.`;
    } else if (data.invoice_email && data.invoice_email.sent === false && data.invoice_email.reason !== 'unmapped_customer') {
      successMsg += '\n\n⚠️ Inter-company invoice email failed to send. Check material_transfer_emails audit table.';
    }
    await ui.alert(successMsg);
    if (!data.transfer_id) return { done: true };
    if (!printPdf) return { done: true, receiptUrl: transferReceiptUrl(data.transfer_id) };
    try {
      const receiptRes = await fetch(transferReceiptUrl(data.transfer_id));
      if (!receiptRes.ok) {
        await ui.alert('Could not generate a receipt for this transfer. Please find it in the transaction history later.');
      } else {
        printPdf(await receiptRes.blob());
      }
    } catch (receiptError) {
      console.error('Failed to fetch or print receipt:', receiptError);
      await ui.alert('An error occurred while generating the receipt.');
    }
    return { done: true };
  } catch (err) {
    const errorMessage = err.message || 'An unknown error occurred during the transfer.';
    // parseApiError already leads with "Transfer failed: " when it has the API's sentence.
    const shown = /^Transfer failed\b/i.test(errorMessage) ? errorMessage : 'Transfer failed: ' + errorMessage;
    await ui.alert(shown);
    return { done: false, message: shown };
  } finally {
    setBusy(false);
  }
}
